import { useCallback, useRef, useState } from 'react';
import { useAuth } from './useAuth';
import { useLanguage } from '../contexts/LanguageContext';
import { blobToBase64, createWavBlob, formatRecordingTime } from '../utils/audioWav';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const MAX_AUDIO_SIZE_MB = 25;

type RecorderHandle = { stop: () => void };

export function useVoiceDictation(onTranscribed: (text: string) => void, options?: {
  maxSeconds?: number;
  onError?: (code: string) => void;
}) {
  const maxSeconds = options?.maxSeconds ?? 120;
  const onError = options?.onError;
  const { apiCall } = useAuth();
  const { language } = useLanguage();
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const recorderRef = useRef<RecorderHandle | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const transcribe = useCallback(async (blob: Blob) => {
    const sizeMb = blob.size / (1024 * 1024);
    if (sizeMb > MAX_AUDIO_SIZE_MB) {
      onError?.('audio_too_large');
      return;
    }
    setIsTranscribing(true);
    try {
      const base64 = await blobToBase64(blob);
      const response = await apiCall(`${API_BASE}/api/v1/transcribe`, {
        method: 'POST',
        body: JSON.stringify({
          audio_base64: base64,
          audio_format: 'wav',
          audio_language: language || 'de',
        }),
      });
      if (!response.ok) return;
      const result = await response.json();
      const text = (result.transcribed_text || '').trim();
      if (text) onTranscribed(text);
    } finally {
      setIsTranscribing(false);
    }
  }, [apiCall, language, onTranscribed, onError]);

  const stopRecording = useCallback(() => {
    if (recorderRef.current) {
      recorderRef.current.stop();
      recorderRef.current = null;
    }
    setIsRecording(false);
    clearTimer();
  }, [clearTimer]);

  const startRecording = useCallback(async () => {
    if (isRecording || isTranscribing) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      const chunks: Float32Array[] = [];

      processor.onaudioprocess = (e) => {
        chunks.push(new Float32Array(e.inputBuffer.getChannelData(0)));
      };
      source.connect(processor);
      processor.connect(audioContext.destination);

      recorderRef.current = {
        stop: () => {
          processor.disconnect();
          source.disconnect();
          stream.getTracks().forEach(track => track.stop());
          audioContext.close();
          const wavBlob = createWavBlob(chunks, audioContext.sampleRate);
          void transcribe(wavBlob);
        },
      };

      setIsRecording(true);
      setRecordingTime(0);
      intervalRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev + 1 >= maxSeconds) {
            stopRecording();
            return maxSeconds;
          }
          return prev + 1;
        });
      }, 1000);
    } catch {
      throw new Error('mic_denied');
    }
  }, [isRecording, isTranscribing, maxSeconds, stopRecording, transcribe]);

  const toggleRecording = useCallback(async () => {
    if (isRecording) {
      stopRecording();
      return;
    }
    await startRecording();
  }, [isRecording, startRecording, stopRecording]);

  return {
    isRecording,
    isTranscribing,
    recordingTime,
    formatRecordingTime,
    startRecording,
    stopRecording,
    toggleRecording,
  };
}
